import { i18n } from 'i18n';
import { gb } from 'growthbook/initGrowthBook';
import { GB_KEYS } from 'growthbook/growthbook.keys';
import { fetchData } from 'framework/api/fetch-data';
import { registerRequestInterceptor, registerResponseObserver } from 'framework/api/interceptors';
import { IFetchDataRequest, IFetchDataResponse, OrderModificationGuardAction, ORDER_MODIFICATION_GUARD_ACTIONS, RequestTag } from 'framework/api/types';
import ApplicationMonitoring from 'framework/applicationMonitoring/ApplicationMonitoring';
import { askConfirm } from 'stores/confirmDialog';
import { ORDER_IN_MODIFICATION } from './activeModificationSession';

const FLAG_MAP_MAX_SIZE = 500;

const flagMap = new Map<string, boolean>();
let installed = false;

const recordFlag = (ref: string, flag: boolean | undefined): void => {
  if (!ref) return;
  if (flagMap.size >= FLAG_MAP_MAX_SIZE && !flagMap.has(ref)) {
    const firstKey = flagMap.keys().next().value;
    if (firstKey !== undefined) flagMap.delete(firstKey);
  }
  flagMap.set(ref, flag === true);
};

// ---------------------------------------------------------------------------
// Mutation matching — tag-based
//
// Call sites that can race an open customer modification session attach a
// `requestTag` from ORDER_MODIFICATION_GUARD_ACTIONS. Other RequestTag
// categories are ignored.
// ---------------------------------------------------------------------------

type MutationDomain = 'om' | 'of';

interface MutationMatch {
  domain: MutationDomain;
  tag: OrderModificationGuardAction;
}

const GUARDED_ACTIONS = new Set<string>(ORDER_MODIFICATION_GUARD_ACTIONS);

const isOrderModificationGuardAction = (tag: RequestTag): tag is OrderModificationGuardAction =>
  GUARDED_ACTIONS.has(tag);

const matchMutation = (request: IFetchDataRequest): MutationMatch | undefined => {
  const { requestTag } = request;
  if (!requestTag || !isOrderModificationGuardAction(requestTag)) return undefined;
  const domain = requestTag.split(':')[0] as MutationDomain;
  return { domain, tag: requestTag };
};

const getPathAfterOrigin = (url: string): string => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
};

const OM_BASE = '/api/orders';
const OF_BASE = '/api/order-fulfillment/orders';

// ---------------------------------------------------------------------------
// Order-reference extraction
// ---------------------------------------------------------------------------

const extractOrderRefs = (
  match: MutationMatch,
  request: IFetchDataRequest
): string[] => {
  const path = getPathAfterOrigin(request.url);
  const base = match.domain === 'of' ? OF_BASE : OM_BASE;
  const suffix = path.slice(base.length);

  const segments = suffix.split('/').filter(Boolean);
  if (segments.length >= 2) {
    return [segments[0]];
  }

  const { body } = request;
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (Array.isArray(b.orderReferences)) return b.orderReferences as string[];
    if (Array.isArray(b.orderIds)) return b.orderIds as string[];
    if (Array.isArray(b.consignments)) {
      return (b.consignments as Array<Record<string, unknown>>)
        .map((c) => (c.orderReference ?? c.orderId) as string)
        .filter(Boolean);
    }
  }

  return [];
};

// ---------------------------------------------------------------------------
// Live re-read
// ---------------------------------------------------------------------------

const liveCheck = async (ref: string, domain: MutationDomain): Promise<boolean> => {
  const endpoint =
    domain === 'of'
      ? `/api/order-fulfillment/orders/${encodeURIComponent(ref)}`
      : `/api/orders/${encodeURIComponent(ref)}`;

  try {
    const result = await fetchData<Record<string, unknown>>({
      url: `${getBaseApiUrl()}${endpoint}`,
      skipInterceptors: true,
    });

    if (!result.success) return false;

    const flag = extractActiveModificationFlag(result.data, domain);
    if (flag !== undefined) recordFlag(ref, flag);
    return flag === true;
  } catch (err) {
    ApplicationMonitoring.trackEvent(
      'modificationGuard',
      'liveCheckError',
      String(err)
    );
    return false;
  }
};

const extractActiveModificationFlag = (
  data: unknown,
  domain: MutationDomain,
): boolean | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as Record<string, unknown>;

  if (domain === 'of') {
    const nested = d.orderDetails as Record<string, unknown> | undefined;
    return nested?.activeModificationSession as boolean | undefined;
  }

  return d.activeModificationSession as boolean | undefined;
};

// ---------------------------------------------------------------------------
// baseApiUrl accessor (avoids circular import of config)
// ---------------------------------------------------------------------------

let cachedBaseApiUrl: string | undefined;

const getBaseApiUrl = (): string => {
  if (!cachedBaseApiUrl) {
    cachedBaseApiUrl =
      process.env.NODE_ENV === 'production'
        ? ((window as unknown as Record<string, unknown>).baseApiUrl as string)
        : (process.env.REACT_APP_BASE_API_URL as string);
  }
  return cachedBaseApiUrl ?? '';
};

// ---------------------------------------------------------------------------
// Guard interceptor
// ---------------------------------------------------------------------------

const guardInterceptor = async (
  request: IFetchDataRequest
): Promise<IFetchDataResponse<unknown> | undefined> => {
  if (!gb.isOn(GB_KEYS.ADMIN_ORDER_ACTIVE_MODIFICATION_SESSION)) return undefined;

  const match = matchMutation(request);
  if (!match) return undefined;

  const refs = extractOrderRefs(match, request);
  if (refs.length === 0) return undefined;

  const knownFlagged = refs.filter((ref) => flagMap.get(ref) === true);

  if (knownFlagged.length > 0) {
    return (await promptUser(refs.length))
      ? undefined
      : abortedResponse();
  }

  if (gb.isOn(GB_KEYS.ADMIN_ORDER_MODIFICATION_LIVE_CHECK)) {
    try {
      const checks = await Promise.all(
        refs.map((ref) => liveCheck(ref, match.domain))
      );
      if (checks.some(Boolean)) {
        return (await promptUser(refs.length)) ? undefined : abortedResponse();
      }
    } catch (err) {
      ApplicationMonitoring.trackEvent(
        'modificationGuard',
        'liveCheckBatchError',
        String(err)
      );
    }
  }

  return undefined;
};

const promptUser = (orderCount: number): Promise<boolean> => {
  const warningKey =
    orderCount > 1
      ? 'generic.status.ordersInModificationWarning'
      : ORDER_IN_MODIFICATION.warningKey;

  return askConfirm({
    warning: i18n.t(warningKey),
    title: 'generic.confirmation',
  });
};

const abortedResponse = (): IFetchDataResponse<unknown> => ({
  success: false,
  aborted: true,
  metadata: {},
});

// ---------------------------------------------------------------------------
// Response observer — only the four WYN-84113 endpoints that return the flag
// ---------------------------------------------------------------------------

const lastPathSegment = (url: string): string => {
  const segments = getPathAfterOrigin(url).split('/').filter(Boolean);
  return segments[segments.length - 1] ?? '';
};

const observeResponse = (
  request: IFetchDataRequest,
  response: IFetchDataResponse<unknown>
): void => {
  if (!response.success || !response.data) return;

  switch (request.requestTag) {
    case 'om:orderList':
      observeOMList(response.data);
      break;
    case 'om:orderDetail': {
      const ref = lastPathSegment(request.url);
      if (ref) observeOMDetail(response.data, ref);
      break;
    }
    case 'of:orderList':
      observeOFList(response.data);
      break;
    case 'of:orderDetail': {
      const id = lastPathSegment(request.url);
      if (id) observeOFDetail(response.data, id);
      break;
    }
  }
};

const observeOMList = (data: unknown): void => {
  if (!data || typeof data !== 'object') return;
  const d = data as Record<string, unknown>;
  const items = d.items as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(items)) return;
  for (const item of items) {
    const ref = (item.orderRef ?? item.orderReference) as string | undefined;
    const flag = item.activeModificationSession as boolean | undefined;
    if (ref && flag !== undefined) recordFlag(ref, flag);
  }
};

const observeOMDetail = (data: unknown, urlRef: string): void => {
  if (!data || typeof data !== 'object') return;
  const flag = (data as Record<string, unknown>).activeModificationSession as boolean | undefined;
  if (flag !== undefined) recordFlag(urlRef, flag);
};

const observeOFList = (data: unknown): void => {
  if (!data || typeof data !== 'object') return;
  const d = data as Record<string, unknown>;
  const items = d.orders as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(items)) return;
  for (const item of items) {
    const id = item.id as string | undefined;
    const flag = item.activeModificationSession as boolean | undefined;
    if (id && flag !== undefined) recordFlag(id, flag);
  }
};

const observeOFDetail = (data: unknown, urlId: string): void => {
  if (!data || typeof data !== 'object') return;
  const nested = (data as Record<string, unknown>).orderDetails as Record<string, unknown> | undefined;
  const flag = nested?.activeModificationSession as boolean | undefined;
  if (flag !== undefined) recordFlag(urlId, flag);
};

// ---------------------------------------------------------------------------
// Public bootstrap
// ---------------------------------------------------------------------------

export const installModificationGuard = (): void => {
  if (installed) {
    return;
  }
  installed = true;
  registerRequestInterceptor(guardInterceptor);
  registerResponseObserver(observeResponse);
};

export const resetModificationGuardForTests = (): void => {
  flagMap.clear();
  installed = false;
};
